import find from "lodash/find"
import map from "lodash/map"
import { makeStyles } from "tss-react/mui"
import { Highlight, useHits } from "react-instantsearch"
import { getWindow } from "utils-lib/getWindow"
import { Link } from "component-lib/Base"
import { useRef } from "react"
import { pushQueryResultData } from "./analytics/analytics"

const ssrWindow = getWindow()
const isSupportPage = ssrWindow.location.href.indexOf("support") >= 0

const useStyles = makeStyles()((_theme, { isHeader }) => ({
  heading: {
    color: `#64696F`,
  },
  results: {
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflow: "visible",
    "& .ais-Highlight, .ais-Highlight-nonHighlighted": {
      fontSize: `inherit !important`,
    },
    "& .ais-Highlight-nonHighlighted": {
      fontSize: `inherit`,
    },
    "& .ais-Highlight-highlighted": {
      background: `transparent`,
      color: `#005D02`,
      fontStyle: `normal`,
      fontSize: `inherit`,
      paddingTop: 0,
      paddingRight: 0,
      paddingLeft: 0,

      marginRight: 0,
      marginLeft: 0,
    },
  },
  result: {
    "&:hover, &.focused": isHeader
      ? {
          background: `#EAF3E8`,
        }
      : {
          "& .Result__title": {
            color: `#1d890a`,
            textDecoration: "underline",
          },
        },
    "&.focused .Result__title": {
      textDecoration: "underline",
    },
    "& .btn": {
      borderRadius: 0,
    },
  },
  link: {
    "&:focus": {
      outline: `2px auto -webkit-focus-ring-color !important`,
    },
  },
}))

const Result = ({
  hit,
  sendEvent,
  hideKeywords,
  hideDescription,
  smallDescription,
  isHeader,
  className = ``,
  filtersToRename = () => {},
  dataIndex = 0,
}) => {
  if (!hit) {
    return null
  }

  const { classes } = useStyles({
    isHeader,
  })

  const keywordsArr =
    hit.keywords && hit.keywords !== `undefined`
      ? hit.keywords.split(",").slice(0, 3)
      : []

  const getAnalyticsData = () => {
    pushQueryResultData(hit, isSupportPage)
    ssrWindow.location = hit.path
  }

  const resultRef = useRef(null)

  return (
    <div className={`wm-grey-light ${classes.results}`} data-testid="Result">
      <div className="maax-font-weight-regular">
        <div className={`${classes.result} ${className}`} ref={resultRef}>
          <div className="mx-3">
            <Link
              className={`wm-border-bottom a-taggable w-100 ${
                isHeader ? `py-2` : `py-3`
              } ${classes.link}`}
              analytics_key={
                isSupportPage
                  ? `support-search-query-result-selection`
                  : `global-search-query-result-selection`
              }
              href={hit.path}
              key={hit.path}
              isExternalLink
              data-index={dataIndex}
              onClick={() => {
                sendEvent("click", hit, "Result Clicked")
                getAnalyticsData()
              }}
              data-testid="ResultLink"
              onFocus={() => {
                resultRef.current.classList.add("focused")
              }}
              onBlur={() => {
                resultRef.current.classList.remove("focused")
              }}
            >
              <div className="px-3">
                <div className="wm-grey-light">
                  <div className="maax-font-weight-bold">
                    <Highlight
                      className="Result__title"
                      attribute="title"
                      hit={hit}
                      data-testid="ResultTitle"
                    />
                  </div>
                  {!hideDescription &&
                    hit.description &&
                    hit.description !== "null" && (
                      <div
                        className={`mt-1 mb-1 show-ellipses ${
                          smallDescription ? `font-sm` : ``
                        }`}
                      >
                        <Highlight
                          attribute="description"
                          hit={hit}
                          data-testid="ResultDescription"
                        />
                      </div>
                    )}
                </div>
                {!!keywordsArr.length && !hideKeywords && (
                  <div className="font-xs wm-link show-ellipses">
                    in{" "}
                    {map(keywordsArr, (item1, index) => {
                      const renamed = find(
                        filtersToRename,
                        (item) =>
                          item.originalName.toLowerCase() ===
                          item1.trim().toLowerCase(),
                      )

                      const name = renamed ? renamed.newName : item1.trim()

                      return (
                        <span key={index}>
                          {name}
                          {index < keywordsArr.length - 1 ? `, ` : ``}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Result.propTypes = {
//   hit: PropTypes.object.isRequired,
//   sendEvent: PropTypes.func.isRequired,
//   hideKeywords: PropTypes.bool.isRequired,
//   hideDescription: PropTypes.bool.isRequired,
//   smallDescription: PropTypes.bool.isRequired,
//   isHeader: PropTypes.bool.isRequired,
//   className: PropTypes.string,
//   filtersToRename: PropTypes.func,
//   dataIndex: PropTypes.number,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Result.defaultProps = {
//   className: ``,
//   filtersToRename: () => {},
//   dataIndex: 0,
// }

const Results = (props) => {
  const propsWithDefaults = {
    ...props,
    className: props.className || ``,
    hideKeywords: props.hideKeywords || true,
    hideDescription: props.hideDescription || true,
    smallDescription: props.smallDescription || true,
    isHeader: props.isHeader || true,
  }

  const {
    indexName,
    numberToRender,
    className,
    hideKeywords,
    hideDescription,
    smallDescription,
    isHeader,
  } = propsWithDefaults

  const { hits, sendEvent } = useHits(propsWithDefaults)

  return hits
    .slice(
      0,
      numberToRender || numberToRender === 0 ? numberToRender : hits.length,
    )
    .map((hit, index) => (
      <Result
        dataIndex={index}
        key={`${indexName}-${index}`}
        className={className}
        hideKeywords={hideKeywords}
        hideDescription={hideDescription}
        smallDescription={smallDescription}
        isHeader={isHeader}
        hit={hit}
        sendEvent={sendEvent}
      />
    ))
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// Results.propTypes = {
//   indexName: PropTypes.string.isRequired,
//   numberToRender: PropTypes.number.isRequired,
//   className: PropTypes.string,
//   hideKeywords: PropTypes.bool,
//   hideDescription: PropTypes.bool,
//   smallDescription: PropTypes.bool,
//   isHeader: PropTypes.bool,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// Results.defaultProps = {
//   className: ``,
//   hideKeywords: true,
//   hideDescription: true,
//   smallDescription: true,
//   isHeader: true,
// }

export { Result }

export default Results
